import { useState } from 'react'
import {
  useCloseIntranetPoll,
  useCreateIntranetPoll,
  useDeleteIntranetPoll,
  useIntranetGroups,
  useIntranetPolls,
  useVoteIntranetPoll
} from '../api/queries'
import type { IntranetPoll } from '../api/types'
import { useCreateRequest } from '../lib/createRequest'
import { formatDateTime } from '../lib/format'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Hurtige afstemninger (docs/DECISIONS.md beslutning 116): alle kan spørge huset - eller en arbejdsgruppe - og
 * resultatet står live. Opretteren vælger, om den er hemmelig (kun tallene) eller åben (navnene vises).
 */
export function PollsTab() {
  const [includeClosed, setIncludeClosed] = useState(false)
  const { data, isLoading, error } = useIntranetPolls(includeClosed)
  const [creating, setCreating] = useState(false)

  useCreateRequest(['afstemning'], () => setCreating(true))

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
          />
          Vis også lukkede
        </label>
        {!creating && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            Ny afstemning
          </button>
        )}
      </div>

      {creating && <PollForm onClose={() => setCreating(false)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && (
        <EmptyState>Ingen afstemninger lige nu. Tryk "Ny afstemning" for at spørge huset om noget.</EmptyState>
      )}

      {data?.map((poll) => (
        <PollCard key={poll.id} poll={poll} />
      ))}
    </div>
  )
}

function PollForm({ onClose }: { onClose: () => void }) {
  const create = useCreateIntranetPoll()
  const { data: groups } = useIntranetGroups()
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [allowMultiple, setAllowMultiple] = useState(false)
  const [isSecret, setIsSecret] = useState(false)
  const [closesOn, setClosesOn] = useState('')
  const [groupId, setGroupId] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await create.mutateAsync({
        question: question.trim(),
        options: options.map((o) => o.trim()).filter(Boolean),
        allowMultiple,
        isSecret,
        // Slutdatoen gælder hele dagen - den lukker ved midnat dansk tid.
        closesAt: closesOn ? new Date(`${closesOn}T23:59:59`).toISOString() : null,
        groupId: groupId ? Number(groupId) : null
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Afstemningen kunne ikke gemmes.')
    }
  }

  return (
    <Card title="Ny afstemning">
      <form className="stack" onSubmit={save}>
        <label className="field">
          <span className="field__label">Spørgsmål *</span>
          <input
            required
            maxLength={300}
            placeholder="F.eks. Pizza eller burger fredag?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>

        <div className="field">
          <span className="field__label">Svar * (mindst to)</span>
          {options.map((option, i) => (
            <div key={i} className="row" style={{ gap: 6 }}>
              <input
                maxLength={200}
                placeholder={`Svar ${i + 1}`}
                value={option}
                onChange={(e) => setOptions(options.map((o, j) => (j === i ? e.target.value : o)))}
              />
              {options.length > 2 && (
                <button
                  type="button"
                  className="btn btn--sm"
                  title="Fjern svaret"
                  onClick={() => setOptions(options.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          {options.length < 10 && (
            <div>
              <button type="button" className="btn btn--sm" onClick={() => setOptions([...options, ''])}>
                + Tilføj svar
              </button>
            </div>
          )}
        </div>

        <div className="form-grid">
          <label className="field">
            <span className="field__label">Slutdato</span>
            <input type="date" value={closesOn} onChange={(e) => setClosesOn(e.target.value)} />
            <span className="tiny muted">Tom = den står åben, til du lukker den.</span>
          </label>
          <label className="field">
            <span className="field__label">Hvem er den til?</span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Hele huset</option>
              {(groups ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.icon ? `${group.icon} ` : ''}
                  {group.name} ({group.memberCount})
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} />
          Man må sætte kryds ved flere svar
        </label>
        <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginTop: 3 }}
            checked={isSecret}
            onChange={(e) => setIsSecret(e.target.checked)}
          />
          <span>
            Hemmelig afstemning
            <span className="tiny muted" style={{ display: 'block' }}>
              Så vises kun antallet af stemmer - ikke hvem der stemte hvad.
            </span>
          </span>
        </label>

        <div className="tiny muted">Alle, den er til, får en besked, når du trykker "Start afstemningen".</div>

        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Gemmer ...' : 'Start afstemningen'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

function PollCard({ poll }: { poll: IntranetPoll }) {
  const vote = useVoteIntranetPoll()
  const close = useCloseIntranetPoll()
  const remove = useDeleteIntranetPoll()
  const [error, setError] = useState<string | null>(null)

  const total = poll.options.reduce((sum, o) => sum + o.votes, 0)
  const leading = Math.max(0, ...poll.options.map((o) => o.votes))

  async function choose(optionId: number) {
    setError(null)
    const mine = poll.myOptionIds
    const next = poll.allowMultiple
      ? mine.includes(optionId)
        ? mine.filter((id) => id !== optionId)
        : [...mine, optionId]
      : mine.includes(optionId)
        ? []
        : [optionId]
    try {
      await vote.mutateAsync({ id: poll.id, optionIds: next })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stemmen kunne ikke gemmes.')
    }
  }

  async function run(action: () => Promise<unknown>, confirmText: string) {
    if (!window.confirm(confirmText)) return
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Det gik ikke.')
    }
  }

  return (
    <article className="card card--flat" style={{ padding: 14, opacity: poll.isOpen ? 1 : 0.85 }}>
      <div className="row row--between" style={{ gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>📊 {poll.question}</div>
          <div className="tiny muted">
            {poll.createdBy?.displayName ?? 'Tidligere kollega'} · {formatDateTime(poll.createdAt)}
            {poll.groupName && <> · kun {poll.groupName}</>}
            {poll.isSecret && ' · hemmelig'}
            {poll.allowMultiple && ' · flere svar'}
          </div>
        </div>
        <span className="pill" style={{ background: poll.isOpen ? '#16A34A' : '#64748B', color: '#fff', flexShrink: 0 }}>
          {poll.isOpen ? (poll.closesAt ? `Lukker ${formatDateTime(poll.closesAt)}` : 'Åben') : 'Lukket'}
        </span>
      </div>

      <div className="stack" style={{ gap: 8, marginTop: 12 }}>
        {poll.options.map((option) => {
          const mine = poll.myOptionIds.includes(option.id)
          const share = total === 0 ? 0 : Math.round((option.votes / total) * 100)
          return (
            <div key={option.id}>
              <button
                type="button"
                disabled={!poll.canVote || vote.isPending}
                onClick={() => void choose(option.id)}
                style={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '9px 12px',
                  border: `2px solid ${mine ? 'var(--primary, #2E2A85)' : 'var(--border, #E2E8F0)'}`,
                  borderRadius: 8,
                  background: 'var(--surface, #fff)',
                  cursor: poll.canVote ? 'pointer' : 'default',
                  overflow: 'hidden',
                  color: 'inherit',
                  font: 'inherit'
                }}
              >
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: `${share}%`,
                    background: mine ? 'rgba(46, 42, 133, 0.14)' : 'rgba(100, 116, 139, 0.12)',
                    transition: 'width 0.3s'
                  }}
                />
                <span style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>
                    {poll.allowMultiple ? (mine ? '☑ ' : '☐ ') : mine ? '● ' : '○ '}
                    {option.text}
                    {!poll.isOpen && option.votes === leading && leading > 0 && ' 🏆'}
                  </span>
                  <span className="small muted">
                    {option.votes} · {share} %
                  </span>
                </span>
              </button>
              {option.voters.length > 0 && (
                <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                  {option.voters.map((voter) => (
                    <span key={voter.id} title={voter.displayName}>
                      <Avatar user={voter} size={22} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="tiny muted" style={{ marginTop: 8 }}>
        {poll.voterCount === 1 ? '1 har stemt' : `${poll.voterCount} har stemt`}
        {poll.canVote && (poll.myOptionIds.length > 0 ? ' · tryk igen for at ændre din stemme' : ' · tryk på et svar for at stemme')}
      </div>

      {poll.canManage && (
        <div className="btn-group" style={{ marginTop: 10 }}>
          {poll.isOpen && (
            <button
              className="btn btn--sm"
              disabled={close.isPending}
              onClick={() => void run(() => close.mutateAsync(poll.id), 'Luk afstemningen nu? Så kan der ikke stemmes mere.')}
            >
              Luk afstemningen
            </button>
          )}
          <button
            className="btn btn--sm"
            disabled={remove.isPending}
            onClick={() => void run(() => remove.mutateAsync(poll.id), 'Slet afstemningen med alle stemmer?')}
          >
            Slet
          </button>
        </div>
      )}

      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
    </article>
  )
}
